<template>

  <div class="relative inline-block">
    <button
        @click="toggleNotificationBell()"
        class="relative focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-label="Notifications"
    >
      <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-7 h-7 text-gray-700 dark:text-gray-200"
      >
        <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0M3.124 7.5A8.969 8.969 0 0 1 5.292 3m13.416 0a8.969 8.969 0 0 1 2.168 4.5"
        />
      </svg>
      <span
          v-if="UnReadNotificationCount > 0"
          class="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-semibold rounded-full px-1.5 py-0.5 shadow-md"
      >
        {{ UnReadNotificationCount }}
      </span>
    </button>
    <Transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-1"
    >
      <div
          v-if="isOpen"
          class="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
      >
        <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Notifications</h3>
        </div>
        <div class="max-h-96 overflow-y-auto">
          <div v-if="notifications.data.length === 0" class="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
            No new notifications
          </div>
          <div
              v-for="notification in notifications.data"
              :key="notification.id"
              class="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0 cursor-pointer transition-colors"
          >
            <RouterLink :to="{name:'app.reviews.show',params:{id:notification.review_id}}" class="flex items-start gap-3">
              <div
                  class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                  :class="notification.type === 'success' ? 'bg-green-100 dark:bg-green-900' : notification.type === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900' : 'bg-blue-100 dark:bg-blue-900'"
              >
                <div
                    class="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold">
                  {{ getInitials(notification.full_name) }}
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ notification.full_name }}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {{ notification.title }}
                </p>
                <!-- Stars -->
                <div class="flex items-center gap-1 mb-1">
                  <svg
                      v-for="star in 5"
                      :key="'star-' + star"
                      class="w-4 h-4"
                      fill="currentColor"
                      :class="star <= notification.rate ? 'text-orange-600' : 'text-gray-300'"
                      viewBox="0 0 20 20"
                  >
                    <path
                        d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                    />
                  </svg>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  {{ dayjs(notification.created_at).fromNow() }}
                </p>
              </div>
            </RouterLink>
          </div>
        </div>
        <div class="px-4 py-3 border-t border-gray-200 dark:border-gray-700 text-center">
          <button
              class="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium">
            View all notifications
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import {ref, computed} from 'vue'
import store from "@/store";
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

const isOpen = ref(false)

const notifications = computed(() => store.state.notifications)
const UnReadNotificationCount = computed(() => store.state.UnReadNotificationCount)

function toggleNotificationBell() {
  isOpen.value = !isOpen.value
  if (UnReadNotificationCount.value > 0) {
    store.commit('setUnReadNotificationCount', 0)
    store.dispatch('markNotificationAsRead')
  }
}

const getInitials = (name) =>
    name
        ? name
            .split(' ')
            .map((word) => word[0])
            .join('')
            .toUpperCase()
        : ''
</script>